import { OptionType } from '@evinced-private/ui-common';

import { ConfigurationLabel } from 'src/common/types/ConfigurationTypes';

import { extractLabelKey, isCoreLabel } from '../../helpers/helpers';
import { TestRowSharedType } from '../../helpers/types';
import {
	createCustomFieldAddChange,
	createCustomFieldRemoveChange
} from '../builders/PayloadBuilder';
import { Change } from '../types/PayloadTypes';

const buildLabelMap = (configuredLabels: ConfigurationLabel[] = []): Map<string, string> => {
	const labelMap = new Map<string, string>();
	configuredLabels.forEach((label) => {
		labelMap.set(label.label.toLowerCase(), label.label);
	});
	return labelMap;
};

const getConfiguredKey = (labelKey: string, labelMap: Map<string, string>): string => {
	const lowerKey = labelKey.toLowerCase();
	return labelMap.get(lowerKey) || labelKey;
};

const transformAddition = (
	labelType: OptionType,
	value: OptionType,
	labelMap: Map<string, string>
): Change => {
	const configuredKey = getConfiguredKey(labelType?.value as string, labelMap);
	return createCustomFieldAddChange(configuredKey, value.value as string);
};

export const transformAdditionsForRow = (
	row: TestRowSharedType,
	additions: Array<{ labelType: OptionType; value: OptionType }>,
	configuredLabels: ConfigurationLabel[] = []
): Change[] => {
	if (!additions.length) {
		return [];
	}

	const labelMap = buildLabelMap(configuredLabels);
	const existingLabels = new Set((row?.labels ?? []).map((label: string) => label.toLowerCase()));
	const existingKeys = new Set(
		(row?.labels ?? []).map((label: string) => extractLabelKey(label).toLowerCase())
	);

	return additions
		.filter((addition) => {
			const labelKey = String(addition.labelType.value).toLowerCase();
			const isCore = isCoreLabel(addition.labelType.value as string);
			const labelValue = String(addition.value.value).toLowerCase();
			const fullLabel = `${labelKey}:${labelValue}`;

			if (isCore) {
				return !existingKeys.has(labelKey);
			}
			return !existingLabels.has(fullLabel);
		})
		.map((addition) => transformAddition(addition.labelType, addition.value, labelMap));
};

const transformRemoval = (labelString: string): Change | null => {
	const colonIndex = labelString.indexOf(':');
	if (colonIndex <= 0) {
		return null;
	}
	const key = labelString.substring(0, colonIndex).trim();
	const value = labelString.substring(colonIndex + 1).trim();
	if (!key || !value) {
		return null;
	}
	return createCustomFieldRemoveChange(key, value);
};

export const transformRemovals = (removals: string[]): Change[] => {
	const changes = removals.map((removal) => transformRemoval(removal));
	return changes.filter((change): change is Change => change !== null);
};
